import React, { useState, useRef, useEffect } from 'react';
import { Upload, Printer, Save, Search, Loader2, Plus, Minus, Calendar, DollarSign, Sparkles, FileText, CreditCard } from 'lucide-react';
import type { PrintableRef, Currency } from '@/types';
import PrintableDocument from '@/components/PrintableDocument';
import ReceiptsService from '@/api/receipts.service';
import { DocumentUpload } from '@/components/DocumentUpload';
import DocumentProcessingService from '@/api/documentProcessing.service';
import PaymentMethodsService from '@/api/paymentmethods.service';
import CustomersService from '@/api/customers.service';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Update the Customer type to match the schema
type Customer = {
  id: number;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailAddress: string;
  street: string;
  city: string;
  state: string;
  zipCode?: string;
  country: string;
  companyName?: string;
};

type PaymentMethod = {
  id: number;
  name: string;
  description?: string;
};

export default function DocumentForm() {
  const [currency, setCurrency] = useState<Currency>('USD');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [amount, setAmount] = useState<number>(0);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethodId, setPaymentMethodId] = useState<number>(0);
  const [notes, setNotes] = useState('');
  const [description, setDescription] = useState('');
  
  const [companyDetails, setCompanyDetails] = useState({
    name: '',
    address: '',
    city: '',
    country: '',
    emailAddress: '',
    phoneNumber: '',
    logoImg: ''
  });
  
  const [customerDetails, setCustomerDetails] = useState<Customer>({
    id: 0,
    firstName: '',
    lastName: '',
    phoneNumber: '',
    emailAddress: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    companyName: ''
  });
  
  const [customerData, setCustomerData] = useState<Customer[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [searchCustomer, setSearchCustomer] = useState('');
  const [isSearchingCustomer, setIsSearchingCustomer] = useState(false);

  // AI Processing state
  const [showAiTab, setShowAiTab] = useState(false);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [isApplyingExtraction, setIsApplyingExtraction] = useState(false);

  const printableRef = useRef<PrintableRef>(null);
  const { toast } = useToast();

  const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
    toast({
      title: title,
      description: description,
      variant: toastType === "error" ? "destructive" : "default",
      duration: duration,
    });
  };

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    if (selectedBusiness) {
      setCompanyDetails({
        name: selectedBusiness.name,
        address: selectedBusiness.address,
        city: selectedBusiness.city,
        country: selectedBusiness.country,
        emailAddress: selectedBusiness.emailAddress,
        phoneNumber: selectedBusiness.phoneNumber,
        logoImg: selectedBusiness.logoImg
      });
    }

    // Fetch payment methods
    const fetchPaymentMethods = async () => {
      try {
        const response = await PaymentMethodsService.getAllPaymentMethods();
        if (response.data.result === 'success') {
          setPaymentMethods(response.data.paymentMethods || []);
        }
      } catch (error) {
        console.error('Error fetching payment methods:', error);
      }
    };

    fetchPaymentMethods();
  }, [selectedBusiness]);

  const handleSearchCustomer = async () => {
    if (!searchCustomer.trim()) return;
    
    setIsSearchingCustomer(true);
    try {
      const response = await CustomersService.searchCustomer(selectedBusiness.id, searchCustomer);
      if (response.data.result === 'success') {
        setCustomerData(response.data.customers || []);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
      showToastWithTimeout("error", "Search Failed", "Failed to search customers. Please try again.", 3000);
    } finally {
      setIsSearchingCustomer(false);
    }
  };

  const handlePrint = () => {
    if (printableRef.current) {
      printableRef.current.print();
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    
    if (!amount || amount <= 0) {
      showToastWithTimeout("error", "❌ Receipt Creation Failed", "Please enter a valid amount.", 3000);
      setIsSaving(false);
      return;
    }

    if (!paymentMethodId) {
      showToastWithTimeout("error", "❌ Receipt Creation Failed", "Please select a payment method.", 3000);
      setIsSaving(false);
      return;
    }

    try {
      const newReceipt = {
        receiptNumber: receiptNumber || `REC-${Date.now()}`,
        amount: Math.round(amount * 100), // Convert to cents
        currency,
        paymentMethod: paymentMethodId,
        paymentDate,
        description,
        notes,
        customerId: customerDetails.id || null,
      };

      const response = await ReceiptsService.createReceipt(newReceipt, selectedBusiness.id);
      
      if (response.data.result === 'success') {
        showToastWithTimeout("success", "✅ Receipt Created", "Receipt has been created successfully!", 3000);
        
        // Reset form
        setReceiptNumber('');
        setAmount(0);
        setDescription('');
        setNotes('');
        setCustomerDetails({
          id: 0,
          firstName: '',
          lastName: '',
          phoneNumber: '',
          emailAddress: '',
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: '',
          companyName: ''
        });
        
        // Redirect to receipts list after a short delay
        setTimeout(() => {
          window.location.href = '/receipts';
        }, 2000);
      } else {
        showToastWithTimeout("error", "❌ Receipt Creation Failed", response.data.message || "Failed to create receipt.", 3000);
      }
    } catch (error) {
      console.error('Error creating receipt:', error);
      showToastWithTimeout("error", "❌ Receipt Creation Failed", "An error occurred while creating the receipt.", 3000);
    } finally {
      setIsSaving(false);
    }
  };

  // AI Processing handlers
  const handleExtractionComplete = (data: any) => {
    setExtractedData(data);
    setShowAiTab(true);
    showToastWithTimeout("success", "✨ AI Extraction Complete", "Receipt data has been extracted successfully. Review the extracted data in the 'Extracted Data' tab.", 5000);
  };

  const applyExtractedData = () => {
    if (!extractedData?.structuredData) return;
    
    setIsApplyingExtraction(true);
    const data = extractedData.structuredData;

    try {
      // Map vendor/customer information
      if (data.vendorName || data.customerName) {
        const name = data.vendorName || data.customerName;
        const nameParts = name.split(' ');
        setCustomerDetails(prev => ({
          ...prev,
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          companyName: data.vendorName || data.customerName || ''
        }));
      }

      // Map receipt details
      if (data.invoiceNumber || data.receiptNumber) {
        setReceiptNumber(data.invoiceNumber || data.receiptNumber);
      }

      if (data.invoiceDate || data.receiptDate || data.paymentDate) {
        const dateStr = data.invoiceDate || data.receiptDate || data.paymentDate;
        if (dateStr) {
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            setPaymentDate(date.toISOString().split('T')[0]);
          }
        }
      }

      // Map currency
      if (data.currency) {
        setCurrency(data.currency as Currency);
      }

      // Map amount
      if (data.totalAmount) {
        setAmount(data.totalAmount);
      }

      // Map payment method
      if (data.paymentMethod) {
        const matchingMethod = paymentMethods.find(method => 
          method.name.toLowerCase().includes(data.paymentMethod.toLowerCase())
        );
        if (matchingMethod) {
          setPaymentMethodId(matchingMethod.id);
        }
      }

      // Map description/notes
      if (data.description) {
        setDescription(data.description);
      }

      if (data.notes) {
        setNotes(data.notes);
      }

      showToastWithTimeout("success", "✅ Data Applied", "Extracted data has been applied to the form. Please review and modify as needed.", 3000);
    } catch (error) {
      console.error('Error applying extracted data:', error);
      showToastWithTimeout("error", "❌ Application Failed", "Failed to apply extracted data. Please try again.", 3000);
    } finally {
      setIsApplyingExtraction(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Create Receipt</h1>
          <p className="text-gray-600 mt-1">Record a new payment receipt</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={handlePrint}
            className="flex items-center gap-2 hover:bg-gray-50 transition-colors"
          >
            <Printer className="h-4 w-4" />
            Print Preview
          </Button>
        </div>
      </div>

      {/* Main Form with Tabs */}
      <Tabs defaultValue="manual" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="manual">Manual Entry</TabsTrigger>
          <TabsTrigger value="ai">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Processing
          </TabsTrigger>
          {showAiTab && (
            <TabsTrigger value="extracted">
              Extracted Data
              {extractedData && <span className="ml-2 w-2 h-2 bg-green-500 rounded-full"></span>}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="manual" className="space-y-6">
          {/* Customer Section */}
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-white">
              <CardTitle className="text-lg font-medium text-gray-800">Customer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    type="text"
                    placeholder="Search for customer"
                    value={searchCustomer}
                    onChange={(e) => setSearchCustomer(e.target.value)}
                    className="pl-10 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <Button 
                  onClick={handleSearchCustomer} 
                  disabled={isSearchingCustomer}
                  variant="outline"
                  className="min-w-[100px] bg-white hover:bg-gray-50"
                >
                  {isSearchingCustomer ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
                </Button>
              </div>

              {customerData.length > 0 && (
                <Card className="border border-blue-100 bg-blue-50/50">
                  <CardContent className="p-4">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">Search Results</Label>
                    <select
                      onChange={(e) => {
                        const selectedCustomer = customerData.find((customer) => customer.id === Number(e.target.value));
                        if (selectedCustomer) {
                          setCustomerDetails(selectedCustomer);
                          setCustomerData([]);
                          setSearchCustomer('');
                        }
                      }}
                      className="w-full p-2.5 border border-gray-200 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="" disabled selected>Select a customer</option>
                      {customerData.map((customer) => (
                        <option key={customer.id} value={String(customer.id)}>
                          {customer.companyName || `${customer.firstName} ${customer.lastName}`} - {customer.emailAddress}
                        </option>
                      ))}
                    </select>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="customerName" className="text-sm font-medium text-slate-700">Customer Name</Label>
                  <Input
                    id="customerName"
                    placeholder="Customer name"
                    className="border-slate-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                    value={customerDetails.companyName || `${customerDetails.firstName} ${customerDetails.lastName}`.trim()}
                    onChange={(e) => {
                      const name = e.target.value;
                      setCustomerDetails(prev => ({
                        ...prev,
                        companyName: name
                      }));
                    }}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="customerEmail" className="text-sm font-medium text-slate-700">Customer Email</Label>
                  <Input
                    id="customerEmail"
                    type="email"
                    placeholder="customer@example.com"
                    className="border-slate-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                    value={customerDetails.emailAddress}
                    onChange={(e) => setCustomerDetails(prev => ({ ...prev, emailAddress: e.target.value }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Receipt Information */}
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-white">
              <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Receipt Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="receiptNumber" className="text-sm font-medium text-slate-700">Receipt Number</Label>
                  <Input
                    id="receiptNumber"
                    placeholder="REC-001"
                    className="border-slate-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                    value={receiptNumber}
                    onChange={(e) => setReceiptNumber(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="paymentDate" className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-primary" />
                    Payment Date
                  </Label>
                  <Input
                    id="paymentDate"
                    type="date"
                    className="border-slate-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Details */}
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 bg-gradient-to-r from-green-50 to-white">
              <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-green-500" />
                Payment Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="amount" className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    <DollarSign className="h-4 w-4 text-green-500" />
                    Amount
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                      {currency === 'USD' ? '$' : currency === 'ZAR' ? 'R' : 'ZWG'}
                    </span>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      className="pl-8 border-slate-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                      value={amount || ''}
                      onChange={(e) => setAmount(Number(e.target.value))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currency" className="text-sm font-medium text-slate-700">Currency</Label>
                  <Select value={currency} onValueChange={(value: Currency) => setCurrency(value)}>
                    <SelectTrigger className="border-slate-200 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="ZWG">ZWG</SelectItem>
                      <SelectItem value="ZAR">ZAR (R)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paymentMethod" className="text-sm font-medium text-slate-700">Payment Method</Label>
                  <Select value={paymentMethodId.toString()} onValueChange={(value) => setPaymentMethodId(Number(value))}>
                    <SelectTrigger className="border-slate-200 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue placeholder="Select payment method" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentMethods.map((method) => (
                        <SelectItem key={method.id} value={method.id.toString()}>
                          {method.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Details */}
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 bg-gradient-to-r from-purple-50 to-white">
              <CardTitle className="text-lg font-medium text-gray-800">Additional Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-medium text-slate-700">Description</Label>
                <Input
                  id="description"
                  placeholder="Payment description"
                  className="border-slate-200 focus:border-blue-500 focus:ring-blue-500 transition-colors"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-sm font-medium text-slate-700">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Additional notes"
                  className="w-full min-h-[100px] resize-none border-slate-200 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button 
              className="px-6 shadow-md hover:shadow-lg transition-all"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Receipt
                </>
              )}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="ai" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3 bg-gradient-to-r from-purple-50 to-white">
              <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-500" />
                AI-Powered Document Processing
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Upload a receipt document and let our AI extract the data automatically
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              <DocumentUpload
                documentType="receipt"
                businessId={selectedBusiness?.id || 1}
                onExtractionComplete={handleExtractionComplete}
                className="w-full"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {showAiTab && (
          <TabsContent value="extracted" className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3 bg-gradient-to-r from-green-50 to-white">
                <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-green-500" />
                  Extracted Data Review
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Review the AI-extracted data and apply it to your receipt form
                </p>
              </CardHeader>
              <CardContent className="pt-6">
                {extractedData ? (
                  <div className="space-y-6">
                    {/* Confidence Score */}
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div>
                        <h3 className="font-medium text-blue-900">Extraction Confidence</h3>
                        <p className="text-sm text-blue-700">
                          AI confidence level for the extracted data
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                        {Math.round((extractedData.confidenceScore || 0) * 100)}%
                      </Badge>
                    </div>

                    {/* Extracted Data Preview */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h3 className="font-medium text-gray-900">Customer Information</h3>
                        <div className="space-y-2 text-sm">
                          <div><strong>Name:</strong> {extractedData.structuredData?.vendorName || extractedData.structuredData?.customerName || 'Not detected'}</div>
                          <div><strong>Address:</strong> {extractedData.structuredData?.vendorAddress || 'Not detected'}</div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h3 className="font-medium text-gray-900">Receipt Details</h3>
                        <div className="space-y-2 text-sm">
                          <div><strong>Number:</strong> {extractedData.structuredData?.invoiceNumber || extractedData.structuredData?.receiptNumber || 'Not detected'}</div>
                          <div><strong>Date:</strong> {extractedData.structuredData?.invoiceDate || extractedData.structuredData?.receiptDate || extractedData.structuredData?.paymentDate || 'Not detected'}</div>
                          <div><strong>Amount:</strong> {extractedData.structuredData?.totalAmount ? `${extractedData.structuredData.currency || 'USD'} ${extractedData.structuredData.totalAmount}` : 'Not detected'}</div>
                          <div><strong>Payment Method:</strong> {extractedData.structuredData?.paymentMethod || 'Not detected'}</div>
                        </div>
                      </div>
                    </div>

                    {/* Apply Button */}
                    <div className="flex justify-end pt-4 border-t border-gray-200">
                      <Button 
                        onClick={applyExtractedData}
                        disabled={isApplyingExtraction}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {isApplyingExtraction ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Applying Data...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Apply Extracted Data
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No extracted data available. Please process a document first.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Hidden Printable Document */}
      <div className="hidden">
        <PrintableDocument
          ref={printableRef}
          document={{
            id: 'temp-id',
            number: receiptNumber,
            type: 'receipt',
            date: new Date().toLocaleDateString('en-GB'),
            currency,
            items: [{
              name: description || 'Payment',
              description: description || 'Payment received',
              quantity: 1,
              unitPrice: amount
            }],
            status: 'paid',
            companyDetails,
            customerDetails: {
              name: customerDetails.companyName || `${customerDetails.firstName} ${customerDetails.lastName}`,
              address: `${customerDetails.street}, ${customerDetails.city}, ${customerDetails.state}, ${customerDetails.country}`,
              emailAddress: customerDetails.emailAddress,
              companyName: customerDetails.companyName
            },
            notes,
            terms: '',
            logo: companyDetails.logoImg,
            signature: '',
            taxes: [],
            issueDate: paymentDate.split('-').reverse().join('/'),
            businessTaxInfos: [],
            customerTaxInfos: []
          }}
        />
      </div>
    </div>
  );
} 